import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { api, submitTestimonial } from "../lib/api";
import { TemperMark } from "./Logo";
import { FadeUp, MaskedLines } from "./Reveal";

const EMPTY = { name: "", role: "", organization: "", testimony: "" };

export default function Testimonials() {
  const [approved, setApproved] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [phase, setPhase] = useState("form");
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/testimonials")
      .then((r) => setApproved(r.data.testimonials || []))
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid = form.name.trim().length > 1 && form.testimony.trim().length > 10;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || phase === "saving") return;
    setPhase("saving");
    setError("");
    try {
      await submitTestimonial(form);
      setPhase("sent");
    } catch {
      setPhase("form");
      setError("Couldn't send your testimony — please try again.");
    }
  };

  return (
    <section data-testid="testimonials" className="border-t border-line py-24 md:py-32">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <FadeUp>
          <p className="eyebrow text-ember">03 — Kind words</p>
          <MaskedLines
            className="mt-6 font-display text-4xl font-extrabold tracking-tight md:text-6xl"
            lines={["Proof over promises."]}
          />
        </FadeUp>

        {approved.length > 0 && (
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {approved.map((t, i) => (
              <FadeUp key={t.id} delay={i * 0.06} className="h-full">
                <figure
                  data-testid="testimonial-card"
                  className="flex h-full flex-col border border-line p-8 transition-colors duration-300 hover:border-ink/40"
                >
                  <TemperMark className="h-3 w-3 text-ember" />
                  <blockquote className="mt-6 flex-1 font-display text-lg font-medium leading-relaxed tracking-tight text-ink md:text-xl">
                    “{t.testimony}”
                  </blockquote>
                  <figcaption className="mt-8">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink">{t.name}</p>
                    <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-steel">
                      {[t.role, t.organization].filter(Boolean).join(" · ") || "Temper client"}
                    </p>
                  </figcaption>
                </figure>
              </FadeUp>
            ))}
          </div>
        )}

        <div className="mt-16 grid gap-12 border border-line p-8 md:grid-cols-12 md:p-12">
          <div className="md:col-span-5">
            <FadeUp>
              <h3 className="font-display text-2xl font-extrabold tracking-tight md:text-3xl">
                Worked with Eric? <span className="text-ember">Leave a testimony.</span>
              </h3>
              <p className="mt-4 leading-relaxed text-steel">
                A few honest words about what changed for you or your team. Eric reviews
                every testimony before it appears on this page.
              </p>
            </FadeUp>
          </div>
          <div className="md:col-span-7" data-testid="testimonial-panel">
            {phase === "sent" ? (
              <div className="flex h-full min-h-[220px] flex-col items-start justify-center">
                <span className="flex h-12 w-12 items-center justify-center border border-ember text-ember" data-testid="testimonial-success-icon">
                  <Check className="h-5 w-5" />
                </span>
                <h4 className="mt-6 font-display text-2xl font-extrabold tracking-tight" data-testid="testimonial-success-title">
                  Thank you — received.
                </h4>
                <p className="mt-3 leading-relaxed text-steel">
                  Eric will review your testimony and publish it here if it fits.
                </p>
                <button
                  data-testid="testimonial-send-another"
                  onClick={() => {
                    setForm(EMPTY);
                    setPhase("form");
                  }}
                  className="btn-outline mt-8"
                >
                  Leave another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="testimonial-form" className="grid gap-6">
                <div className="grid gap-6 md:grid-cols-3">
                  <label className="block">
                    <span className="eyebrow text-ink/50">Name *</span>
                    <input data-testid="testimonial-name" className="field-input mt-2" placeholder="Your name" value={form.name} onChange={set("name")} />
                  </label>
                  <label className="block">
                    <span className="eyebrow text-ink/50">Role</span>
                    <input data-testid="testimonial-role" className="field-input mt-2" placeholder="e.g. COO" value={form.role} onChange={set("role")} />
                  </label>
                  <label className="block">
                    <span className="eyebrow text-ink/50">Organization</span>
                    <input data-testid="testimonial-organization" className="field-input mt-2" placeholder="Company" value={form.organization} onChange={set("organization")} />
                  </label>
                </div>
                <label className="block">
                  <span className="eyebrow text-ink/50">Your testimony *</span>
                  <textarea
                    data-testid="testimony-text"
                    className="field-input mt-2 min-h-[90px] resize-y"
                    placeholder="What changed for you or your team?"
                    value={form.testimony}
                    onChange={set("testimony")}
                  />
                </label>
                {error && (
                  <p data-testid="testimonial-error" className="text-sm text-forge">
                    {error}
                  </p>
                )}
                <div>
                  <button
                    type="submit"
                    data-testid="testimonial-submit"
                    disabled={!valid || phase === "saving"}
                    className={`btn ${valid ? "btn-ink" : "cursor-not-allowed bg-ink/10 text-ink/40"}`}
                  >
                    {phase === "saving" ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                      </>
                    ) : (
                      "Send testimony"
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
